/**
 * PricingEditor — SuperAdmin pricing management
 * Edit plan prices, limits, and feature flags without code changes.
 * Changes propagate to all centres within 5 minutes (planConfigCache TTL).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FiTag } from 'react-icons/fi';
import { getPlanConfig, updatePlanConfig } from '../utils/api';

const PLAN_COLORS = {
  FREE:          { bg: '#F9FAFB', border: '#D1D5DB', badge: '#6B7280' },
  STARTER:       { bg: '#EFF6FF', border: '#93C5FD', badge: '#2563EB' },
  AI_ADMIN:      { bg: '#F5F3FF', border: '#C4B5FD', badge: '#7C3AED' },
  SCALE:         { bg: '#FFFBEB', border: '#FCD34D', badge: '#D97706' },
  SOLO_FREE:     { bg: '#F0FAF6', border: '#6EE7B7', badge: '#059669' },
  SOLO_PLUS:     { bg: '#ECFDF5', border: '#34D399', badge: '#047857' },
  SOLO_PRO:      { bg: '#D1FAE5', border: '#10B981', badge: '#065F46' },
  SOLO_AI_ADMIN: { bg: '#064E3B', border: '#059669', badge: '#065F46' },
};

const FEATURE_LABELS = {
  healingRequests: 'Healing Requests',
  classBooking:    'Class Booking',
  phIntro:         'PH Introduction',
  voiceAssistant:  'Voice Assistant',
  intakeForm:      'Intake Form',
};

const NUM_FIELDS = [
  { field: 'monthlyPriceINR',     label: 'Monthly Price (₹)',    placeholder: '0',         step: '1' },
  { field: 'gstPercent',          label: 'GST % *',              placeholder: '18',        step: '1', max: 100 },
  { field: 'maxHealers',          label: 'Max Healers',           placeholder: 'Unlimited', step: '1' },
  { field: 'maxPatients',         label: 'Max Patients',          placeholder: 'Unlimited', step: '1' },
  { field: 'chatQueriesPerMonth', label: 'Chat Queries / Month',  placeholder: 'Unlimited', step: '1' },
];

// Trim whitespace before coercion — Number(" ") = 0, not null, without this guard
const toNum = (v) => (String(v).trim() === '' ? null : Number(String(v).trim()));

export default function PricingEditor() {
  const [plans,       setPlans]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [editState,   setEditState]   = useState({});
  const [saving,      setSaving]      = useState(null);
  const [saveError,   setSaveError]   = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res     = await getPlanConfig();
      const fetched = res.data.data.plans;
      setPlans(fetched);
      const init = {};
      fetched.forEach(p => {
        init[p._id] = {
          monthlyPriceINR:     p.monthlyPriceINR     ?? '',
          gstPercent:          p.gstPercent          ?? '',
          maxHealers:          p.maxHealers          ?? '',
          maxPatients:         p.maxPatients         ?? '',
          chatQueriesPerMonth: p.chatQueriesPerMonth ?? '',
          features: { ...p.features },
        };
      });
      setEditState(init);
    } catch (err) {
      console.error('[PricingEditor] load failed', err);
      setError('Could not load plan configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleField(planId, field, value) {
    setEditState(prev => ({ ...prev, [planId]: { ...prev[planId], [field]: value } }));
  }

  function handleFeature(planId, key, checked) {
    setEditState(prev => ({
      ...prev,
      [planId]: { ...prev[planId], features: { ...prev[planId].features, [key]: checked } },
    }));
  }

  async function handleConfirmSave() {
    if (!confirmPlan) return;
    const planId = confirmPlan._id;
    const edit   = editState[planId];
    const gst    = toNum(edit.gstPercent);
    if (gst === null) {
      setSaveError('GST % is required — enter 0 if the plan is exempt.');
      setConfirmPlan(null);
      return;
    }
    if (gst < 0 || gst > 100) {
      setSaveError('GST % must be between 0 and 100.');
      setConfirmPlan(null);
      return;
    }
    setSaving(planId);
    setSaveError(null);
    try {
      await updatePlanConfig(planId, {
        monthlyPriceINR:     toNum(edit.monthlyPriceINR),
        gstPercent:          gst,
        maxHealers:          toNum(edit.maxHealers),
        maxPatients:         toNum(edit.maxPatients),
        chatQueriesPerMonth: toNum(edit.chatQueriesPerMonth),
        features: edit.features,
      });
      await load();
      setConfirmPlan(null);
    } catch (err) {
      console.error('[PricingEditor] save failed', err);
      setSaveError(err?.response?.data?.message || 'Could not save changes.');
      setConfirmPlan(null);
    } finally {
      setSaving(null);
    }
  }

  if (loading) return <div style={s.center}>Loading plan configuration…</div>;
  if (error)   return <div style={{ ...s.center, color: '#DC2626' }}>{error}</div>;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>Plan Pricing</h2>
        <p style={s.subtitle}>Changes take effect for all new signups within 5 minutes.</p>
      </div>

      {saveError && <div style={s.saveError}>{saveError}</div>}

      {[
        { groupKey: 'centre', groupLabel: 'CENTRE PLANS',      items: plans.filter(p => !p.planName.startsWith('SOLO_')) },
        { groupKey: 'solo',   groupLabel: 'SOLO HEALER PLANS', items: plans.filter(p =>  p.planName.startsWith('SOLO_')) },
      ].filter(g => g.items.length > 0).map(({ groupKey, groupLabel, items }) => (
        <div key={groupKey} style={s.section}>
          <div style={s.sectionLabel}>{groupLabel}</div>
          <div style={s.grid}>
            {items.map(plan => {
              const c        = PLAN_COLORS[plan.planName] || PLAN_COLORS.FREE;
              const edit     = editState[plan._id] || {};
              const isSaving = saving === plan._id;
              return (
                <div key={plan._id} style={{ ...s.card, background: c.bg, borderColor: c.border }}>
                  <div style={s.cardHead}>
                    <span style={{ ...s.badge, background: c.badge }}>{plan.planName}</span>
                    <span style={s.updated}>
                      Updated {new Date(plan.updatedAt).toLocaleDateString('en-IN')}
                    </span>
                  </div>
                  {NUM_FIELDS.map(({ field, label, placeholder, step, max }) => (
                    <div key={field} style={s.fieldRow}>
                      <label style={s.label}>{label}</label>
                      <input
                        style={s.input}
                        type="number"
                        min="0"
                        max={max ?? undefined}
                        step={step}
                        placeholder={placeholder}
                        value={edit[field] ?? ''}
                        onChange={e => handleField(plan._id, field, e.target.value)}
                      />
                    </div>
                  ))}
                  <p style={s.featuresLabel}>Features</p>
                  {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                    <label key={key} style={s.featureRow}>
                      <input
                        type="checkbox"
                        checked={edit.features?.[key] ?? false}
                        onChange={e => handleFeature(plan._id, key, e.target.checked)}
                        style={{ marginRight: 8 }}
                      />
                      {label}
                    </label>
                  ))}
                  <button
                    style={{ ...s.saveBtn, background: c.badge, opacity: isSaving ? 0.6 : 1 }}
                    disabled={isSaving}
                    onClick={() => { setSaveError(null); setConfirmPlan(plan); }}
                  >
                    <FiTag size={13} style={{ marginRight: 6 }} />
                    {isSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {confirmPlan && (
        <div style={s.overlay} onClick={() => setConfirmPlan(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Update {confirmPlan.planName} pricing?</h3>
            <p style={s.modalBody}>
              This affects all new signups and upgrade displays immediately.
            </p>
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setConfirmPlan(null)}>Cancel</button>
              <button
                style={{ ...s.confirmBtn, opacity: saving ? 0.6 : 1 }}
                disabled={!!saving}
                onClick={handleConfirmSave}
              >
                {saving ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page:         { padding: '24px' },
  center:       { padding: '40px', textAlign: 'center', color: '#6B7280' },
  header:       { marginBottom: '24px' },
  title:        { fontSize: '22px', fontWeight: '700', color: '#111827', margin: 0 },
  subtitle:     { fontSize: '13px', color: '#6B7280', marginTop: '6px' },
  saveError:    { background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626',
                  padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' },
  section:      { marginBottom: '32px' },
  sectionLabel: { fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', color: '#6B7280', textTransform: 'uppercase', marginBottom: '12px' },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' },
  card:         { borderRadius: '12px', border: '1.5px solid', padding: '20px',
                  display: 'flex', flexDirection: 'column', gap: '12px' },
  cardHead:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  badge:        { color: '#fff', fontWeight: '700', fontSize: '12px', padding: '3px 10px',
                  borderRadius: '20px', letterSpacing: '0.5px' },
  updated:      { fontSize: '11px', color: '#9CA3AF' },
  fieldRow:     { display: 'flex', flexDirection: 'column', gap: '4px' },
  label:        { fontSize: '12px', fontWeight: '600', color: '#374151' },
  input:        { border: '1px solid #D1D5DB', borderRadius: '6px', padding: '7px 10px',
                  fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  featuresLabel:{ fontSize: '12px', fontWeight: '600', color: '#374151', margin: '4px 0 2px' },
  featureRow:   { display: 'flex', alignItems: 'center', fontSize: '13px', color: '#374151',
                  cursor: 'pointer' },
  saveBtn:      { color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 16px',
                  fontWeight: '600', fontSize: '13px', cursor: 'pointer', marginTop: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' },
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:        { background: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '380px',
                  width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalTitle:   { fontSize: '17px', fontWeight: '700', color: '#111827', margin: '0 0 10px' },
  modalBody:    { fontSize: '14px', color: '#6B7280', margin: '0 0 20px' },
  modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  cancelBtn:    { padding: '9px 18px', border: '1px solid #D1D5DB', borderRadius: '8px',
                  background: '#fff', fontSize: '14px', cursor: 'pointer', fontWeight: '500' },
  confirmBtn:   { padding: '9px 18px', border: 'none', borderRadius: '8px',
                  background: '#7C3AED', color: '#fff', fontSize: '14px',
                  fontWeight: '600', cursor: 'pointer' },
};
